// Booking-flow helpers that wrap the capacity + hold RPCs exposed by the
// Supabase schema. Kept thin so the mobile app (future) can import the
// same signatures against its own Supabase client.

import { supabase } from "../config";
import { rememberPaystackHop } from "./charge-guard";
import { initAnswerRefusal } from "./paystack-init-error";
import {
  checkCapacity as sharedCheckCapacity,
  createBookingHold as sharedCreateBookingHold,
  fetchSeatAvailability as sharedFetchSeatAvailability,
  cancelCartBooking as sharedCancelCartBooking,
  extendCartHold as sharedExtendCartHold,
  getCartHoldState as sharedGetCartHoldState,
  type CartHoldStateResult,
  type ExtendCartHoldResult,
  fetchRoomAvailability as sharedFetchRoomAvailability,
  type BookingWindow,
  type CapacityResponse,
  type HoldFailure,
  type HoldResponse,
} from "@vertu/shared";

// Canonical booking types + RPC wrappers live in @vertu/shared/booking-core
// (parity Phase 0.5) — the staff app binds the SAME functions to its own
// client, so the two apps can no longer drift. The bindings below keep the
// widget's historical one-argument call signatures.
export type { BookingWindow, CapacityResponse, HoldFailure, HoldResponse };

export function checkCapacity(input: {
  serviceId: string;
  floorPlanId: string | null;
  elementId: string | null;
  window: BookingWindow;
}): Promise<CapacityResponse> {
  return sharedCheckCapacity(supabase, input);
}

// ─── Early guest identity ──────────────────────────────────────────────────
// Set once by GuestProvider when the guest introduces themselves (the
// identity sheet, a checkout edit, or an OTP sign-in). Every hold call site
// passes guest {null,null,null} — historically identity only existed in the
// checkout form — so the wrappers below substitute this instead of touching
// eight call sites. Holds created BEFORE capture are back-filled by
// setCartGuestContact.
type HoldGuest = { name: string | null; email: string | null; phone: string | null };
let currentHoldGuest: HoldGuest | null = null;
export function setGuestForHolds(guest: HoldGuest | null): void {
  currentHoldGuest =
    guest && (guest.name || guest.email || guest.phone) ? guest : null;
}
function withGuestIdentity(guest: HoldGuest): HoldGuest {
  if (guest.name || guest.email || guest.phone) return guest;
  return currentHoldGuest ?? guest;
}

export function createBookingHold(input: {
  serviceId: string;
  floorPlanId: string | null;
  elementId: string | null;
  serviceUnitId: string | null;
  seatingAreaId: string | null;
  window: BookingWindow;
  guest: { name: string | null; email: string | null; phone: string | null };
  cartToken: string;
  holdMinutes?: number;
  /** Special dates with guest menus: how many guests take each (20260925090010). */
  menuLines?: { menu_id: string; qty: number }[] | null;
}): Promise<HoldResponse> {
  return sharedCreateBookingHold(supabase, {
    ...input,
    guest: withGuestIdentity(input.guest),
  });
}

// Update an existing pending booking hold's window + party size in
// place. Wraps public.update_booking_hold (added in
// 20260516000007_update_booking_hold.sql). Used by the cart's per-item
// editable controls so a customer can amend a slot without removing
// and re-adding the row.
export type UpdateHoldResponse =
  | {
      ok: true;
      booking_id: string;
      starts_at: string;
      ends_at: string;
      party_size: number;
      hold_expires_at: string;
      // Re-priced for the new party (20260702000030). Optional for back-compat
      // with pre-migration deployments, which omit these — the cart then keeps
      // the original amount.
      amount_cents?: number;
      unit_price_cents?: number;
      requires_full_payment?: boolean;
      currency?: string;
    }
  // hint: a refusal RAISED by a trigger (party_not_allowed from
  // trg_enforce_special_date_party fires on update of party_size too)
  // carries its wording as the PostgREST error hint.
  | { ok: false; reason: string; detail?: unknown; hint?: string };

export async function updateBookingHold(input: {
  bookingId: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  holdMinutes?: number;
}): Promise<UpdateHoldResponse> {
  const { data, error } = await supabase.rpc("update_booking_hold", {
    p_booking_id: input.bookingId,
    p_starts_at: input.startsAt,
    p_ends_at: input.endsAt,
    p_party_size: input.partySize,
    p_hold_minutes: input.holdMinutes ?? 15,
    // Possession proof — the RPC rejects a bare booking id (a UUID is not
    // authorization to move someone else's held slot).
    p_cart_token: getCartToken(),
  });
  if (error || !data) {
    return {
      ok: false,
      reason: error?.message ?? "network_error",
      ...(error?.hint ? { hint: error.hint } : {}),
    };
  }
  return data as UpdateHoldResponse;
}

// Event-ticket hold. Wraps the create_event_ticket_hold RPC
// (20260510000002) so the widget can reserve N tickets of one type for
// the duration of the cart's checkout. One booking row per call; multi-
// type purchases happen as multiple calls / multiple cart lines.
export type EventHoldResponse =
  | {
      ok: true;
      booking_id: string;
      event_id: string;
      ticket_type_id: string;
      quantity: number;
      amount_cents: number;
      currency: string;
      requires_full_payment: boolean;
    }
  | HoldFailure;

export async function createEventTicketHold(input: {
  eventId: string;
  ticketTypeId: string;
  quantity: number;
  guest: { name: string | null; email: string | null; phone: string | null };
  cartToken: string;
  holdMinutes?: number;
  /** Per-attendee chosen option ids: [["<pizza>","<wine>"], ...] length=qty. */
  selections?: string[][];
}): Promise<EventHoldResponse> {
  const guest = withGuestIdentity(input.guest);
  const { data, error } = await supabase.rpc("create_event_ticket_hold", {
    p_event_id: input.eventId,
    p_ticket_type_id: input.ticketTypeId,
    p_quantity: input.quantity,
    p_guest_name: guest.name,
    p_guest_email: guest.email,
    p_guest_phone: guest.phone,
    p_cart_token: input.cartToken,
    p_hold_minutes: input.holdMinutes ?? 15,
    p_selections: input.selections ?? [],
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as EventHoldResponse;
}

export function fetchSeatAvailability(input: {
  floorPlanId: string;
  window: BookingWindow;
}): Promise<Set<string>> {
  return sharedFetchSeatAvailability(supabase, input);
}

// ─── Live room availability (Cloudbeds-backed accommodation) ───────────────
// Canonical implementation in @vertu/shared/booking-core (parity Phase 2);
// this binding keeps the widget's one-argument signature.
export type { RoomAvailability } from "@vertu/shared";
export function fetchRoomAvailability(input: {
  serviceId: string;
  window: BookingWindow;
}) {
  return sharedFetchRoomAvailability(supabase, input);
}

// ─── Duration helpers ────────────────────────────────────────────────────
//
// Canonical implementation in @vertu/shared/booking-core (parity Phase 0.5).
// It lived here first, but the staff app and the guest app had each grown
// their own copy — one of them without the tenant's duration_minutes at all —
// so the same booking consumed a different slice of the day depending on
// which surface took it. Re-exported rather than moved, so every existing
// import path still resolves.
export {
  bookingDurationMinutes,
  defaultDurationMinutesFor,
  durationOptionsFor,
} from "@vertu/shared";

export function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

export function humanWindow(starts: string, ends: string, locale = "en-ZA"): string {
  const a = new Date(starts);
  const b = new Date(ends);
  const sameDay = a.toDateString() === b.toDateString();
  const day = a.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const t1 = a.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
  const t2 = b.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false });
  if (!sameDay) {
    const day2 = b.toLocaleDateString(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    // A stay is priced and understood in NIGHTS — the guest counts nights,
    // not the two clock times at either end. Counted on calendar days so a
    // 15:00 → 11:00 stay reads 1 night, not 0.
    const nights = Math.max(
      1,
      Math.round(
        (new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime() -
          new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime()) /
          86400000,
      ),
    );
    return `${day} ${t1} → ${day2} ${t2} · ${nights} night${nights === 1 ? "" : "s"}`;
  }
  return `${day} · ${t1}–${t2}`;
}

// Local-time formatter for <input type="datetime-local"> (no zone, minutes precision).
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export type ConfirmResponse =
  | { ok: true; booking_ids: string[]; required_cents: number }
  | { ok: false; reason: string; required_cents?: number; paid_cents?: number };

export async function confirmBookingsForCart(input: {
  cartToken: string;
  guest: { name: string; email: string; phone: string; notes?: string };
  paidAmountCents: number;
  paymentStatus?: "paid" | "unpaid";
}): Promise<ConfirmResponse> {
  const { data, error } = await supabase.rpc("confirm_bookings_for_cart", {
    p_cart_token: input.cartToken,
    p_guest_name: input.guest.name,
    p_guest_email: input.guest.email,
    p_guest_phone: input.guest.phone,
    p_notes: input.guest.notes ?? null,
    p_paid_amount_cents: input.paidAmountCents,
    p_payment_status: input.paymentStatus ?? "paid",
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as ConfirmResponse;
}

// ─── Paystack payment flow ────────────────────────────────────────────────

export type AppliedCode = {
  code: string;
  kind: "voucher" | "gift_card";
  discount_cents: number;
  voucher_id?: string;
};

export type PaymentIntentResponse =
  | {
      ok: true;
      // Free / fully-discounted order: the cart nets to 0, so the server has
      // already confirmed the bookings (no Paystack hop). Skip checkout and go
      // straight to the confirmation view.
      no_payment_required?: boolean;
      payment_id: string;
      reference: string;
      amount_cents: number;
      cart_sum_cents?: number;
      discount_cents?: number;
      applied_codes?: AppliedCode[];
      currency: string;
      required_cents: number;
      deposit_cents: number;
      booking_count: number;
    }
  | {
      ok: false;
      reason: string;
      code?: string;
      detail?: string;
      /** reason='cohost_required' (event co-hosting gate): the required
       *  service's name, for the human error message. */
      service_name?: string;
    };

export async function createBookingPaymentIntent(input: {
  cartToken: string;
  guest: { name: string; email: string; phone: string; notes?: string };
  codes?: string[];
}): Promise<PaymentIntentResponse> {
  const { data, error } = await supabase.rpc("create_booking_payment_intent", {
    p_cart_token: input.cartToken,
    p_guest_name: input.guest.name,
    p_guest_email: input.guest.email,
    p_guest_phone: input.guest.phone,
    p_notes: input.guest.notes ?? null,
    p_codes: input.codes ?? [],
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as PaymentIntentResponse;
}

export type BookingDepositState = {
  ok: boolean;
  deferred?: boolean;
  deposit_deadline_state?: string | null;
  deposit_due_at?: string | null;
  amount_cents?: number;
  currency?: string;
};

// After a hold is created, check whether the advance-deposit policy DEFERRED it
// (a large group booked far ahead → reserve now, pay the deposit later). Keyed
// on the guest's own cart_token, so anon can read only its own hold. Returns
// { ok:false } on any error so callers fall through to normal checkout.
export async function getBookingDepositState(
  bookingId: string,
  cartToken: string,
): Promise<BookingDepositState> {
  const { data, error } = await supabase.rpc("get_booking_deposit_state", {
    p_booking_id: bookingId,
    p_cart_token: cartToken,
  });
  if (error || !data) return { ok: false };
  return data as BookingDepositState;
}

export type RedeemConfirmResponse =
  | {
      ok: true;
      // net=0: codes fully covered the cart; the server redeemed them
      // atomically AND confirmed the bookings — no Paystack.
      no_payment_required: true;
      amount_cents: 0;
      booking_count?: number;
    }
  | {
      ok: true;
      // net>0: nothing was consumed (the redemption rolled back); fall back to
      // the normal Paystack intent for the remainder.
      no_payment_required: false;
      needs_payment: true;
      reason?: string;
    }
  | {
      ok: false;
      reason: string;
      code?: string;
      /** reason='cohost_required': required service name (see intent). */
      service_name?: string;
      /** reason='earlier_payment_unverified' (20260928090040): this cart's
       *  card payments to ask Paystack about first; elsewhere=true when one
       *  is on another cart of the same guest and slot (never named). */
      references?: string[];
      elsewhere?: boolean;
      /** reason='earlier_payment_succeeded': the card payment that went
       *  through, when it is this cart's. reason='same_slot_already_paid':
       *  this guest's slot is already paid on another cart (never named);
       *  confirm again with allowSameSlot for a deliberate second booking. */
      reference?: string | null;
    };

// Atomic redeem-first confirm for a cart the codes drive to NET ZERO. Redeems
// voucher/gift-card in one transaction and confirms in-widget when net=0; on
// net>0 it rolls the redemption back and returns needs_payment so the caller
// falls back to createBookingPaymentIntent. Only call when the preview net is 0.
export async function redeemAndConfirmCart(input: {
  cartToken: string;
  guest: { name: string; email: string; phone: string; notes?: string };
  codes?: string[];
  /** The guest pressed "Book another" (same_slot_already_paid): a second
   *  booking for a slot already paid on another cart is deliberate. Sent
   *  only when true; it passes that one refusal, nothing else. */
  allowSameSlot?: boolean;
}): Promise<RedeemConfirmResponse> {
  const { data, error } = await supabase.rpc("redeem_and_confirm_cart", {
    p_cart_token: input.cartToken,
    p_guest_name: input.guest.name,
    p_guest_email: input.guest.email,
    p_guest_phone: input.guest.phone,
    p_notes: input.guest.notes ?? null,
    p_codes: input.codes ?? [],
    ...(input.allowSameSlot === true ? { p_allow_same_slot: true } : {}),
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as RedeemConfirmResponse;
}

// ─── Guest country capture (for the Cloudbeds reservation push) ────────────
// Stores the guest's country (ISO code) on the pending cart bookings, scoped by
// cart_token. Called at checkout when the cart has an accommodation item so the
// Cloudbeds reservation books with a real country rather than the ZA default.
export async function setCartGuestCountry(input: {
  cartToken: string;
  country: string;
}): Promise<void> {
  try {
    await supabase.rpc("set_cart_guest_country", {
      p_cart_token: input.cartToken,
      p_country: input.country,
    });
  } catch {
    // Non-fatal — country is reservation metadata; falls back to the default.
  }
}

// ─── Guest contact capture (abandoned-cart identity) ───────────────────────
// Stamps the guest's name/email/phone onto every pending hold in the cart —
// including combo holds, whose RPC takes no guest params at insert time.
// Token possession is the authorisation, same as country above. Best-effort:
// identity also travels with each new hold via setGuestForHolds, and checkout
// captures it authoritatively.
export async function setCartGuestContact(input: {
  cartToken: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}): Promise<void> {
  if (!input.name && !input.email && !input.phone) return;
  try {
    await supabase.rpc("set_cart_guest_contact", {
      p_cart_token: input.cartToken,
      p_name: input.name,
      p_email: input.email,
      p_phone: input.phone,
    });
  } catch {
    // Non-fatal — a hold without identity is what we had before this feature.
  }
}

// ─── Live code validation (no redemption) ──────────────────────────────
// Widget calls this when the customer types a code + hits "Apply". Shows
// the projected discount against the cart's first booking. Redemption
// happens server-side on payment success.
export type CodePreview =
  | {
      ok: true;
      code: string;
      kind: "voucher" | "gift_card";
      discount_cents: number;
      label: string;
    }
  | { ok: false; reason: string };

/** A booking link's promo code, described before anything is held (get_promo_link_terms). */
export async function fetchPromoLinkTerms(tenantId: string, code: string): Promise<PromoLinkTermsAnswer> {
  const { data, error } = await supabase.rpc("get_promo_link_terms", { p_tenant_id: tenantId, p_code: code });
  if (error || !data) return { ok: false, reason: error?.message ?? "network_error" };
  return data as PromoLinkTermsAnswer;
}
export type PromoLinkTermsAnswer =
  | {
      ok: true;
      code: string;
      kind: "percent" | "fixed_amount" | "free_addon";
      discount_percent: number | null;
      discount_cents: number | null;
      applies_to_service_ids: string[];
      applies_to_service_types: string[];
      min_spend_cents: number | null;
      deposit_service_ids: string[];
      deferred_deposit_service_ids: string[];
    }
  | { ok: false; reason: string };

export async function previewCode(input: {
  tenantId: string;
  code: string;
  bookingId: string;
  /** A code from a booking link: a promo code only, never a gift card (link-promo.ts). */
  vouchersOnly?: boolean;
}): Promise<CodePreview> {
  const trimmed = input.code.trim();
  if (!trimmed) return { ok: false, reason: "code_required" };

  const { data: vData } = await supabase.rpc("validate_voucher", {
    p_tenant_id: input.tenantId,
    p_code: trimmed,
    p_booking_id: input.bookingId,
  });
  const v = vData as {
    ok?: boolean;
    voucher_name?: string;
    discount_cents?: number;
    reason?: string;
  } | null;
  if (v?.ok) {
    return {
      ok: true,
      code: trimmed.toUpperCase(),
      kind: "voucher",
      discount_cents: v.discount_cents ?? 0,
      label: v.voucher_name ?? trimmed,
    };
  }

  if (input.vouchersOnly) return { ok: false, reason: v?.reason ?? "not_found" };

  const { data: gData } = await supabase.rpc("check_gift_card_balance", {
    p_tenant_id: input.tenantId,
    p_code: trimmed,
  });
  const g = gData as {
    ok?: boolean;
    remaining_amount_cents?: number;
    currency?: string;
    reason?: string;
  } | null;
  if (g?.ok) {
    return {
      ok: true,
      code: trimmed.toUpperCase(),
      kind: "gift_card",
      discount_cents: g.remaining_amount_cents ?? 0,
      label: `Gift card (${g.currency ?? "ZAR"} ${((g.remaining_amount_cents ?? 0) / 100).toFixed(2)} balance)`,
    };
  }

  return { ok: false, reason: v?.reason ?? g?.reason ?? "not_found" };
}

// ─── Gift-card purchase (widget-initiated) ─────────────────────────────
export type GiftPurchaseIntent =
  | { ok: true; payment_id: string; reference: string; amount_cents: number; currency: string }
  | { ok: false; reason: string };

export async function createGiftCardPaymentIntent(input: {
  tenantId: string;
  amountCents: number;
  purchaser: { name: string; email: string; phone?: string };
  recipient?: { name?: string; email?: string; phone?: string; message?: string };
  expiresAt?: string | null;
}): Promise<GiftPurchaseIntent> {
  const { data, error } = await supabase.rpc("create_gift_card_payment_intent", {
    p_tenant_id: input.tenantId,
    p_amount_cents: input.amountCents,
    p_purchaser_name: input.purchaser.name,
    p_purchaser_email: input.purchaser.email,
    p_purchaser_phone: input.purchaser.phone ?? null,
    p_recipient_name: input.recipient?.name ?? null,
    p_recipient_email: input.recipient?.email ?? null,
    p_recipient_phone: input.recipient?.phone ?? null,
    p_gift_message: input.recipient?.message ?? null,
    p_expires_at: input.expiresAt ?? null,
  });
  if (error || !data) {
    return { ok: false, reason: error?.message ?? "network_error" };
  }
  return data as GiftPurchaseIntent;
}

export type PaystackInitResponse =
  // `reference`: the payment whose page this is. It can be an EARLIER page of
  // this very checkout (the second-charge guard hands that back instead of
  // starting another, 20260928090040), so anything that keeps a reference
  // keeps this one, never the intent's.
  | { ok: true; authorization_url: string; reference: string }
  // `message`: the server's own sentence for the guest, and `earlier_reference`
  // the payment it is about, sent with the guard's refusals.
  | { ok: false; error: string; message?: string; earlier_reference?: string };

// Calls the Paystack-initialize Edge Function. The function loads the
// tenant's secret_key server-side and returns the authorization_url the
// customer gets redirected to.
export async function initializePaystackCheckout(input: {
  reference: string;
  customerEmail: string;
  callbackUrl?: string;
  /** The guest pressed "Book another" (same_slot_already_paid). */
  allowSameSlot?: boolean;
  /**
   * May the guard hand back an earlier page of this same checkout (resume)?
   * Default yes. False asks for a new page: a checkout reopened after the
   * handed-back page itself was not paid (useCheckoutSubmit: freshPage).
   */
  allowResume?: boolean;
}): Promise<PaystackInitResponse> {
  try {
    const { data, error } = await supabase.functions.invoke("paystack-initialize", {
      body: {
        reference: input.reference,
        customer_email: input.customerEmail,
        callback_url: input.callbackUrl ?? window.location.href,
        // This widget follows whatever reference the answer names.
        allow_resume: input.allowResume !== false,
        // Tell this guest when they already paid for the same slot on
        // another cart (a reload's new cart), and, once they pressed "Book
        // another", that the second booking is deliberate (20260928090040).
        check_same_slot: true,
        ...(input.allowSameSlot === true ? { allow_same_slot: true } : {}),
      },
    });
    // A refusal, from a non-2xx answer's body (422 tenant_not_connected, 502
    // card_payments_unavailable, …: supabase-js says only "Edge Function
    // returned a non-2xx status code") or a 2xx ok:false. All of it in
    // state/paystack-init-error.ts, which the logic spec runs.
    const refused = await initAnswerRefusal(data, error);
    if (refused) return refused;
    const reference = typeof data.reference === "string" && data.reference ? data.reference : input.reference;
    rememberPaystackHop(reference);
    return { ok: true, authorization_url: data.authorization_url, reference };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

// Stable per-widget session token so concurrent cart items share a cart_token.
let cartTokenCache: string | null = null;
export function getCartToken(): string {
  if (cartTokenCache) return cartTokenCache;
  cartTokenCache =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `cart-${Math.random().toString(36).slice(2)}-${Date.now()}`;
  return cartTokenCache;
}

/**
 * A fresh cart for this tab: the next hold gets a new cart token. For Choose
 * another time after a refund (20261001160010): the refunded payment stays
 * with its cart, and the webhook's double-charge guard keeps the first
 * payment of a cart and refunds any later one, so a new time paid for on the
 * same cart token would be refunded too. The caller lets go of the old
 * cart's holds first (they are cancelled under the old token).
 */
export function startNewCart(): string {
  cartTokenCache = null;
  return getCartToken();
}

/**
 * A checkout resumed after a reload or the Paystack round trip (the full-page
 * checkout's sessionStorage entry, checked against the server first): its
 * holds live under the token they were made with, so this tab adopts it.
 * Called only while the cart is empty, before this tab has held anything.
 */
export function adoptCartToken(token: string): boolean {
  if (typeof token !== "string" || token.length < 4) return false;
  cartTokenCache = token;
  return true;
}

// Cancels a pending hold when the guest removes its cart line. Without this
// the hold stays alive and checkout — which charges every pending booking
// under the cart_token — would confirm and charge a line the guest removed.
// Best-effort: an already-expired hold returns ok:false, which is fine.
/**
 * Persist the party size the GUEST ENTERED for the whole visit. Multi-option
 * carts create one booking per option whose party_size is that option's
 * quantity — without this, every staff surface had to GUESS the humans from
 * the item counts. Fire-and-forget: a failed stamp degrades to the old
 * inference, never blocks the hold.
 */
export function stampVisitPartySize(partySize: number): void {
  const n = Math.max(1, Math.round(partySize || 0));
  if (!Number.isFinite(n) || n < 1) return;
  supabase
    .rpc("set_cart_party_size", {
      p_cart_token: getCartToken(),
      p_party_size: n,
    })
    .then(undefined, () => {});
}

export function cancelCartBooking(bookingId: string): Promise<void> {
  return sharedCancelCartBooking(supabase, bookingId, getCartToken());
}

// ─── Hold keep-alive (checkout drawer) ──────────────────────────────────────
// The checkout's hold timer (use-hold-keepalive.ts). Both calls are the
// shared booking-core ones bound to this widget's client and cart token; a
// thrown call becomes reason 'unknown', which the timer never reads as a
// lapse.
export type { CartHoldStateResult, ExtendCartHoldResult };

export async function getCartHoldState(bookingIds: readonly string[]): Promise<CartHoldStateResult> {
  try {
    return await sharedGetCartHoldState(supabase, getCartToken(), bookingIds);
  } catch (e) {
    return { ok: false, reason: "unknown", message: (e as Error)?.message ?? "network_error" };
  }
}

export async function extendCartHold(bookingIds: readonly string[]): Promise<ExtendCartHoldResult> {
  try {
    return await sharedExtendCartHold(supabase, getCartToken(), bookingIds);
  } catch (e) {
    return { ok: false, reason: "unknown", message: (e as Error)?.message ?? "network_error" };
  }
}
